import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from '../adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { AzureMariaDBLinkedService } from 'app/model/linkedservice/azure-maria-db.linkedservice';
import { LinkedServiceAdapter } from './linkedservice.adapter';
import { IModel } from 'app/model/base/annotation';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { Constants } from 'app/shared/shared-constants';
import { akvToResource, secretBaseToPasswordOrAKV, akvToModel } from './azure-key-vault.util';
import { isString } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { AdapterContext } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureMariaDBLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: AzureMariaDBLinkedService) => {
                    // if password is in AKV, then connectionString won't contain secret
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV && ls.password instanceof AzureKeyVault) {
                        return '*';
                    }
                    return 'secret';
                },
                pwd: 'secret',
                encryptedCredential: '*'
            }
        }
    }
})
export class AzureMariaDBLinkedServiceAdapter extends LinkedServiceAdapter<AzureMariaDBLinkedService> {
    protected modelConstructor(): new () => AzureMariaDBLinkedService {
        return AzureMariaDBLinkedService;
    }

    protected model(): IModel {
        return AzureMariaDBLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureMariaDBLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.MariaDBLinkedService;
        properties.type = Metamodel.modelFor(AzureMariaDBLinkedService).name;

        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('Server', linkedService.server || '');
            builder.add('Port', linkedService.port ? linkedService.port.toString() : '');
            builder.add('Database', linkedService.databaseName || '');
            builder.add('UID', linkedService.userName || '');
            if (linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                builder.add('PWD', linkedService.password);
            }
            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
            }
            connectionString = linkedService.server === Constants.maskedCredential &&
                linkedService.port === undefined &&
                linkedService.databaseName === Constants.maskedCredential &&
                linkedService.userName === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.password === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }

        properties.typeProperties = {
            connectionString,
            pwd: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureMariaDBLinkedService): Promise<AzureMariaDBLinkedService> {
        const properties = linkedServiceResource.properties as Model.MariaDBLinkedService;

        linkedService.server = undefined;
        linkedService.port = undefined;
        linkedService.databaseName = undefined;
        linkedService.userName = undefined;
        linkedService.password = undefined;

        if (isString(properties.typeProperties.connectionString)) {
            const builder = new DbConnectionStringBuilderService();
            builder.parse(properties.typeProperties.connectionString as string);

            linkedService.server = builder.getValue('Server', false) || '';
            linkedService.port = parseInt(builder.getValue('Port', false), 10) || undefined;
            linkedService.databaseName = builder.getValue('Database', false) || '';
            linkedService.userName = builder.getValue('UID', false) || '';
            linkedService.password = Constants.maskedCredential;
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Server', 'Port', 'Database', 'UID', 'PWD']);
            if (properties.typeProperties.pwd) {
                linkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.pwd, false, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.server = Constants.maskedCredential;
            linkedService.databaseName = Constants.maskedCredential;
            linkedService.userName = Constants.maskedCredential;
            linkedService.password = Constants.maskedCredential;
            linkedService.port = undefined;
            if (properties.typeProperties.pwd) {
                linkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.pwd, false, this.context);
            }
        } else if (properties.typeProperties.connectionString
            && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAKV = akvToModel(properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
            linkedService.enableAKV = true;
        }
        return linkedService;
    }
}
